import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  VolumeUp as VolumeUpIcon,
  List as ListIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, doc, getDoc, getDocs, addDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const WordList = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [currentTab, setCurrentTab] = useState(0)
  const [words, setWords] = useState([])
  const [savedWords, setSavedWords] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState('A1')
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredWords, setFilteredWords] = useState([])
  const [currentPage, setCurrentPage] = useState('word-list')

  useEffect(() => {
    if (user && userData) {
      setCurrentLevel(userData.level || 'A1')
      loadWords(userData.level || 'A1')
      loadSavedWords(userData.level || 'A1')
    }
  }, [user, userData])

  useEffect(() => {
    // Arama filtreleme
    if (currentTab === 0) {
      const filtered = words.filter(word => 
        word.word?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        word.meaning?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredWords(filtered)
    } else {
      const filtered = savedWords.filter(word => 
        word.word?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        word.meaning?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredWords(filtered)
    }
  }, [searchQuery, words, savedWords, currentTab])

  const loadWords = async (level) => {
    try {
      setLoading(true)
      console.log('Loading words for level:', level)
      
      // Android uygulamasındaki aynı yapıyı kullan
      const firestoreLevel = level.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'wordpairs' : 'wordPairs'
      
      const levelsRef = collection(db, 'words', firestoreLevel, 'levels')
      const levelsSnapshot = await getDocs(levelsRef)
      
      const allWords = []
      
      levelsSnapshot.forEach(doc => {
        const data = doc.data()
        const levelWords = data[fieldName] || []
        
        if (levelWords.length > 0) {
          allWords.push(...levelWords)
        }
      })
      
      console.log('Words loaded:', allWords.length)
      setWords(allWords)
      setFilteredWords(allWords)
    } catch (error) {
      console.error('Error loading words:', error)
      toast.error('Kelimeler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadSavedWords = async (level) => {
    try {
      if (!user) return
      
      const savedWordsRef = collection(db, 'users', user.uid, 'savedWords', level, 'words')
      const savedWordsSnapshot = await getDocs(savedWordsRef)
      
      const savedWordsList = []
      savedWordsSnapshot.forEach(doc => {
        savedWordsList.push({ id: doc.id, ...doc.data() })
      })
      
      console.log('Saved words loaded:', savedWordsList.length)
      setSavedWords(savedWordsList)
    } catch (error) {
      console.error('Error loading saved words:', error)
    }
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
    setSearchQuery('')
  }

  const handleBackToWordList = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('word-list')
    }
  }

  const speakWord = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'en-US'
      utterance.rate = 0.8
      
      window.speechSynthesis.speak(utterance)
      toast.success('Kelime okunuyor...')
    } else {
      toast.error('Sesli okuma bu tarayıcıda desteklenmiyor')
    }
  }

  const addWordToSaved = async (word) => {
    try {
      if (!user) {
        toast.error('Giriş yapmanız gerekiyor')
        return
      }

      const savedWordsRef = collection(db, 'users', user.uid, 'savedWords', currentLevel, 'words')
      await addDoc(savedWordsRef, word)
      
      toast.success('Kelime başarıyla eklendi!')
      speakWord(word.word)
      
      // Saved words listesini yenile
      loadSavedWords(currentLevel)
    } catch (error) {
      console.error('Error adding word:', error)
      toast.error('Kelime eklenirken hata oluştu')
    }
  }

  const isWordSaved = (word) => {
    return savedWords.some(savedWord => 
      savedWord.word === word.word && savedWord.meaning === word.meaning
    )
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kelime Listesi
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card - Reading sayfasındaki gibi */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ListIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Kelime Listesi
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviye: {currentLevel}
            </Typography>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: '#1976d2',
                  fontWeight: 'bold'
                },
                '& .Mui-selected': {
                  color: '#1976d2'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2'
                }
              }}
            >
              <Tab label="Kelime Listesi" />
              <Tab label="Çalışılanlar" />
            </Tabs>
          </CardContent>
        </Card>

        {/* Search */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Kelime ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1565c0'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Words List */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        ) : filteredWords.length === 0 ? (
          <Card sx={{ mx: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <ListIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {currentTab === 0 ? 'Henüz Kelime Yok' : 'Henüz Çalışılan Kelime Yok'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentTab === 0 
                  ? 'Bu seviye için henüz kelime eklenmemiş'
                  : 'Henüz kelime çalışmamışsınız'
                }
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ 
            mx: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {currentTab === 0 ? 'Kelimeler' : 'Çalışılan Kelimeler'} ({filteredWords.length})
              </Typography>
              <List>
                {filteredWords.map((word, index) => (
                  <ListItem key={index} sx={{ 
                    border: '1px solid #e0e0e0', 
                    mb: 1, 
                    borderRadius: 1,
                    backgroundColor: isWordSaved(word) ? '#f3f8ff' : 'white'
                  }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium" color="primary">
                          {word.word}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {word.meaning}
                          </Typography>
                          {word.pronunciation && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              /{word.pronunciation}/
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => speakWord(word.word)}
                          sx={{
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: '#f3f8ff'
                            }
                          }}
                        >
                          <VolumeUpIcon sx={{ fontSize: 16 }} />
                        </Button>
                        {currentTab === 0 && !isWordSaved(word) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => addWordToSaved(word)}
                            sx={{
                              backgroundColor: '#1976d2',
                              '&:hover': {
                                backgroundColor: '#1565c0'
                              }
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </Button>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
          {currentTab === 0 
            ? 'Kelimeleri dinlemek için ses butonuna tıklayın. Çalışmak istediğiniz kelimeleri "Çalışılanlar" sekmesine ekleyin.'
            : 'Çalıştığınız kelimeleri burada görebilirsiniz. Yeni kelimeler eklemek için "Kelime Listesi" sekmesine gidin.'
          }
        </Alert>
      </Container>
    </SpaceTheme>
  )
}

export default WordList
